import { defineStore } from 'pinia'
import type { Ecom } from '@serato/sws-sdk/types/Ecom'
import { CART_UUID_STORAGE_KEY } from '@/utils/const'
import {
  getCartWarningMessage,
  hasCartWarningMessage,
  shouldPersistPostCheckoutWarning
} from '@/utils/cartWarningMessages'
import type { BillingAddressFieldName } from '@/components/cards/billing-details-card/BillingDetailsForm.vue'

interface CartState {
  cartLoading: boolean
  cartValidationErrors: Map<BillingAddressFieldName, string>
  cartFetchAttempted: boolean,
  cart: Ecom.Cart | null
  preCheckoutWarningDismissed: boolean
  /** Persisted when the cart is cleared after a successful order; shown on /success/:id */
  postCheckoutWarningCode: number | null
  postCheckoutWarningForOrderId: number | null
}

export const useCartStore = defineStore('CartStore', {
  state: (): CartState => ({
    cartLoading: false,
    cartValidationErrors: new Map(),
    cartFetchAttempted: false,
    cart: null,
    preCheckoutWarningDismissed: false,
    postCheckoutWarningCode: null,
    postCheckoutWarningForOrderId: null
  }),
  getters: {
    items: (state) => state.cart?.items ?? [],
    totalPrice: (state) => (state.cart?.total_amount ?? 0).toFixed(2),
    subtotalAmount: (state) => (state.cart?.subtotal_amount ?? 0).toFixed(2),
    taxAmount: (state) => (state.cart?.tax_amount ?? 0).toFixed(2),
    taxRate: (state) => state.cart?.items[0].tax_rate ?? 0,
    currency: (state) => state.cart?.currency ?? '',
    billingAddress: (state) => state.cart?.billing_address ?? { country_code: 'US' },
    couponCode: (state) => state.cart?.coupon_code ?? '',
    /** Returns true if the cart is for a subscription */
    isSubscription: (state) => state.cart?.items.length === 1 && state.cart?.items[0].subscription_billing_period !== undefined,
    /** Shortcut to get the country **/
    billingAddressCountryCode: (state) => state.cart?.billing_address?.country_code ?? '',
    isBillingAddressCountryUS (): boolean {
      return this.billingAddressCountryCode === 'US'
    },
    isBillingAddressCountryUSorCA (): boolean {
      return ['US', 'CA'].includes(this.billingAddressCountryCode)
    },
    hasCartValidationErrors: (state) => [...state.cartValidationErrors.values()].some(errorMessage => errorMessage !== ''),
    showPreCheckoutWarning: (state) =>
      hasCartWarningMessage(state.cart?.items[0]?.warning_code, 'pre-checkout') &&
      !state.preCheckoutWarningDismissed,
    postCheckoutWarningMessageForOrder: (state) => (orderId: number) => {
      if (state.postCheckoutWarningForOrderId !== orderId) {
        return null
      }
      return getCartWarningMessage(state.postCheckoutWarningCode, 'post-checkout')
    }
  },
  actions: {
    setCart (cart: Ecom.Cart) {
      if (cart.items.length === 0) {
        this.clearCart()
        return
      }
      const previousCartUuid = this.cart?.uuid
      this.cart = cart
      localStorage.setItem(CART_UUID_STORAGE_KEY, cart.uuid)

      if (previousCartUuid !== cart.uuid) {
        this.preCheckoutWarningDismissed = false
        this.clearPostCheckoutWarning()
      }
    },
    clearCart () {
      this.cart = null
      this.preCheckoutWarningDismissed = false
      this.clearPostCheckoutWarning()
      localStorage.removeItem(CART_UUID_STORAGE_KEY)
    },
    /**
     * Clears the cart after payment succeeds and preserves any post-checkout warning for the success page.
     */
    clearCartAfterSuccessfulOrder (orderId: number) {
      const warningCode = this.cart?.items[0]?.warning_code
      if (shouldPersistPostCheckoutWarning(warningCode)) {
        this.postCheckoutWarningCode = warningCode!
        this.postCheckoutWarningForOrderId = orderId
      }

      this.cart = null
      this.preCheckoutWarningDismissed = false
      localStorage.removeItem(CART_UUID_STORAGE_KEY)
    },
    clearPostCheckoutWarning () {
      this.postCheckoutWarningCode = null
      this.postCheckoutWarningForOrderId = null
    },
    dismissPreCheckoutWarning () {
      this.preCheckoutWarningDismissed = true
    }
  }
})
